import classNames from "classnames";
import useMediaQuery from "hooks/useMediaQuery";
import React, { useLayoutEffect, useRef, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import {
	Area,
	CartesianGrid,
	ComposedChart,
	LabelList,
	Legend,
	Line,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { Dispatch } from "redux";
import { IStore } from "redux/store";

// TODO: Can be reused
const renderLineLabel = (props: any) => {
	const { x, y, value } = props;
	return (
		<text x={x} y={y} dy={-10} fill="#666" fontSize={12} textAnchor="middle">
			{value}
		</text>
	);
};

// TODO: Can be reused
const CustomizedAxisTick = (props: any) => {
	const { x, y, payload } = props;
	// Split the label string by the newline character
	const [month, year] = payload.value.split(" ");

	return (
		<g transform={`translate(${x},${y})`}>
			<text
				x={0}
				y={0}
				dy={16}
				textAnchor="middle"
				fill="#6b7280"
				fontSize={12}
			>
				{/* Render month and year on separate lines using tspan */}
				<tspan textAnchor="middle" x="0">
					{month}
				</tspan>
				<tspan textAnchor="middle" x="0" dy="15">
					{year}
				</tspan>
			</text>
		</g>
	);
};

// TODO: Can be reused
const CustomTooltip = ({ active, payload, label }: any) => {
	if (active && payload && payload.length) {
		// The payload can contain multiple entries for the same dataKey (e.g., from Area and Line).
		// We create a unique list based on the 'name' prop to avoid duplicates.
		const uniquePayload = payload
			.reduce((acc: any[], current: any) => {
				if (!acc.find((item) => item.name === current.name)) {
					acc.push(current);
				}
				return acc;
			}, [])
			.filter((item: any) => item?.stroke !== "none");

		return (
			<div className="p-3 bg-white border border-gray-200 rounded-lg shadow-lg">
				<p className="font-medium text-gray-800 mb-2">{label}</p>
				{uniquePayload.map(
					(pld: any, index: number) =>
						pld.name && (
							<p key={index} style={{ color: pld.color }} className="text-sm">
								{`${pld.name}: ${pld.value.toLocaleString()}`}
							</p>
						)
				)}
			</div>
		);
	}

	return null;
};

// TODO: Can be reused
const CustomLegend = (props: any) => {
	let { payload } = props;
	payload = payload.filter((data: any) => data?.payload?.name);
	return (
		<div className="flex justify-end items-center space-x-4">
			{payload.map(
				(entry: any, index: number) =>
					// We use the legendType property to filter out items we don't want in the legend
					entry.type !== "none" && (
						<div
							key={`item-${index}`}
							className="flex items-center space-x-2 cursor-pointer"
						>
							<div
								style={{
									width: 10,
									height: 10,
									borderRadius: "50%",
									backgroundColor: entry.color,
								}}
							></div>
							<span
								style={{ color: entry.color }}
								className="text-sm font-medium"
							>
								{entry.value}
							</span>
						</div>
					)
			)}
		</div>
	);
};

// TODO: Can be reused
const renderRecdLabel = (props: any) => {
	const { x, y, value } = props;
	// Position the label above the point
	const dy = value === 0 ? -10 : -10;
	// For zero values, we want them just above the x-axis line
	const finalY = value === 0 ? y - 5 : y;

	return (
		<text
			x={x}
			y={finalY}
			dy={dy}
			fill="#666"
			fontSize={12}
			textAnchor="middle"
		>
			{value}
		</text>
	);
};

export interface IAreaGraph3Point {
	xAxis: string;
	mecloRob: number;
	mecloRecd: number;
}

const areaGraph1Data: IAreaGraph3Point[] = [
	{ xAxis: "Jan 2024", mecloRob: 20000, mecloRecd: 0 },
	{ xAxis: "Feb 2024", mecloRob: 19700, mecloRecd: 0 },
	{ xAxis: "Mar 2024", mecloRob: 19350, mecloRecd: 13000 },
	{ xAxis: "Apr 2024", mecloRob: 18900, mecloRecd: 0 },
	{ xAxis: "May 2024", mecloRob: 18600, mecloRecd: 0 },
	{ xAxis: "Jun 2024", mecloRob: 18300, mecloRecd: 0 },
	{ xAxis: "Jul 2024", mecloRob: 17950, mecloRecd: 0 },
	{ xAxis: "Aug 2024", mecloRob: 17650, mecloRecd: 0 },
	{ xAxis: "Sep 2024", mecloRob: 17300, mecloRecd: 15000 },
	{ xAxis: "Oct 2024", mecloRob: 17000, mecloRecd: 0 },
	{ xAxis: "Nov 2024", mecloRob: 16700, mecloRecd: 0 },
	{ xAxis: "Dec 2024", mecloRob: 16400, mecloRecd: 0 },
	{ xAxis: "Jan 2025", mecloRob: 16100, mecloRecd: 0 },
	{ xAxis: "Feb 2025", mecloRob: 15800, mecloRecd: 0 },
	{ xAxis: "Mar 2025", mecloRob: 15500, mecloRecd: 0 },
	{ xAxis: "Apr 2025", mecloRob: 15200, mecloRecd: 0 },
	{ xAxis: "May 2025", mecloRob: 14900, mecloRecd: 0 },
	{ xAxis: "Jun 2025", mecloRob: 26600, mecloRecd: 12000 },
	{ xAxis: "Jul 2025", mecloRob: 26300, mecloRecd: 0 },
];

type OwnProps = {};
type AreaGraph1Props = PropsFromRedux & OwnProps;
function AreaGraph1(props: AreaGraph1Props) {
	const lgScreen = 1024;
	const isWidthLessThanLgScreen = useMediaQuery(lgScreen);
	const containerRef = useRef<HTMLDivElement>(null);
	const [isContainerReady, setIsContainerReady] = useState(false);
	useLayoutEffect(() => {
		if (containerRef.current && containerRef.current.clientWidth > 0) {
			setIsContainerReady(true);
		}
	}, []);
	return (
		<div className="overflow-x-auto p-4 shadow-md border border-gray-300 rounded-md mt-4 w-full">
			<div className="w-full">
				<div className="relative">
					<h3 className="text-base lg:text-lg">
						Consumption & Receipt metric by tank type
					</h3>
					<h4 className="absolute text-blue-500 text-sm font-medium">
						Total MECLO Received: 150K
					</h4>
				</div>
				<div
					ref={containerRef}
					className={classNames({ "w-[1000px]": isWidthLessThanLgScreen })}
				>
					{isContainerReady ? (
						<ResponsiveContainer
							className={"w-full"}
							width="100%"
							height={450}
							debounce={5}
						>
							<ComposedChart
								data={areaGraph1Data}
								margin={{
									top: 40,
									right: 30,
									bottom: 20,
									left: 0,
								}}
							>
								{/* Define SVG gradients for area fills */}
								<defs>
									<linearGradient
										id="colorMecloRob"
										x1="0"
										y1="0"
										x2="0"
										y2="1"
									>
										<stop offset="5%" stopColor="#808080" stopOpacity={0.4} />
										<stop offset="95%" stopColor="#808080" stopOpacity={0} />
									</linearGradient>
									<linearGradient
										id="colorMecloRecd"
										x1="0"
										y1="0"
										x2="0"
										y2="1"
									>
										<stop offset="5%" stopColor="#ffc658" stopOpacity={0.8} />
										<stop offset="95%" stopColor="#ffc658" stopOpacity={0.2} />
									</linearGradient>
								</defs>

								<CartesianGrid strokeDasharray="3 3" vertical={true} />
								<XAxis
									dataKey="xAxis"
									padding={{ left: 30 }}
									axisLine={false}
									tickLine={false}
									interval={0}
									style={{ whiteSpace: "pre" }}
									tick={<CustomizedAxisTick />}
								/>
								<YAxis
									domain={[0, 30000]}
									tick={{ fontSize: 12, fill: "#6b7280" }}
									axisLine={false}
									tickLine={false}
								/>

								<Tooltip content={<CustomTooltip />} />
								<Legend
									verticalAlign="top"
									align="right"
									iconType="wye"
									iconSize={10}
									wrapperStyle={{ top: 0, right: 20 }}
									formatter={(value, entry) => {
										const { color } = entry;
										return <span style={{ color }}>{value}</span>;
									}}
									content={<CustomLegend />}
								/>

								<ReferenceLine
									y={20000}
									stroke="#ef4444"
									strokeDasharray="4 4"
								/>

								<Area
									name="MECLO RECD"
									type="step"
									dataKey="mecloRecd"
									stroke="#ffc658"
									strokeWidth={2}
									fill="url(#colorMecloRecd)"
									dot={false}
								>
									<LabelList dataKey="mecloRecd" content={renderRecdLabel} />
								</Area>
								<Area
									// This area provides the grey fill under the line
									type="linear"
									dataKey="mecloRob"
									stroke="none"
									fill="url(#colorMecloRob)"
								/>
								<Line
									name="MECLO ROB"
									type="linear"
									dataKey="mecloRob"
									stroke="#5a6472"
									strokeWidth={2}
									dot={{
										stroke: "#5a6472",
										strokeWidth: 2,
										r: 4,
										fill: "white",
									}}
									activeDot={{ r: 6, strokeWidth: 2, fill: "#5a6472" }}
								>
									<LabelList dataKey="mecloRob" content={renderLineLabel} />
								</Line>
							</ComposedChart>
						</ResponsiveContainer>
					) : null}
				</div>
			</div>
		</div>
	);
}
function mapStateToProps(store: IStore, ownProps: OwnProps) {
	return {};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(AreaGraph1);
